import React, { useCallback, useEffect, useRef, useState } from "react";

const PAGE = {
  width: 595.28,
  height: 841.89,
  margin: 35,
  bottom: 778,
  top: 94,
};
const C = {
  ink: "233B2E",
  muted: "627068",
  orange: "E94126",
  green: "268842",
  line: "DCE5DE",
  pale: "F0F5F1",
  pass: "268842",
  fail: "AF4332",
  review: "8A6725",
  na: "68766C",
  passBg: "EAF4EA",
  failBg: "FBEAE6",
  reviewBg: "FFF4DA",
  white: "FFFFFF",
};
const STATUS = {
  pass: { label: "Fine for these checks", tag: "PASS", colour: C.pass },
  fail: { label: "Needs attention", tag: "FAIL", colour: C.fail },
  review: { label: "Needs a closer look", tag: "REVIEW", colour: C.review },
  na: { label: "Not applicable", tag: "N/A", colour: C.na },
};
const CORE = [
  "front",
  "back",
  "hinge_top",
  "hinge_middle",
  "hinge_bottom",
  "certificate",
];
const PHOTO_NAMES = {
  front: "Front of door",
  back: "Back of door",
  hinge_top: "Top hinge",
  hinge_middle: "Middle hinge",
  hinge_bottom: "Bottom hinge",
  certificate: "Certificate",
};
const SOURCE_NAMES = {
  "resident-report": "Resident-reported check",
  "model-identification": "Photo identification",
  "model-concern": "Possible issue flagged in photo",
  "configured-photo-rule": "Configured photo-screening rule",
  "certificate-text": "Certificate text screening",
  "hinge-text": "Hinge text screening",
  "evidence-required": "Evidence needed",
  "evidence-provided": "Evidence attached",
  "not-requested": "Not requested in this flow",
  conflict: "Conflicting information",
};
const arr = (v) => (Array.isArray(v) ? v : []);
const own = (v, k) => Object.prototype.hasOwnProperty.call(v || {}, k);
const unique = (v) => [...new Set(v.filter(Boolean))];
const clamp = (v, low, high) => Math.max(low, Math.min(high, v));
const isBlob = (v) => typeof Blob !== "undefined" && v instanceof Blob;
const stateOf = (v) => (own(STATUS, v) ? v : "review");
const human = (v) =>
  String(v || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
function clean(value) {
  if (value == null) return "";
  const text = String(value)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
    .replace(/\r\n?/g, "\n")
    .replace(/\t/g, "    ");
  if (text.length > 100000)
    throw new Error(
      "One report field exceeds 100,000 characters. Shorten that field before exporting."
    );
  return text;
}
function dateValue(value) {
  try {
    if (value == null || value === "") return null;
    const result = value?.toDate
      ? value.toDate()
      : typeof value?.seconds === "number"
      ? new Date(value.seconds * 1000)
      : new Date(value);
    return Number.isFinite(result.getTime()) ? result : null;
  } catch (_) {
    return null;
  }
}
function dateText(value, zone = "Europe/London") {
  const date = dateValue(value);
  if (!date) return "Not recorded";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: zone,
      timeZoneName: "short",
    }).format(date);
  } catch (_) {
    return date.toISOString();
  }
}
function mediaUrl(value) {
  const text = typeof value === "string" ? value.trim() : "";
  return /^(https?:\/\/|blob:|data:image\/(jpeg|png|webp);)/i.test(text)
    ? text
    : "";
}
function storedUrl(item) {
  return mediaUrl(
    item?.displayImage?.url ||
      item?.originalImage?.url ||
      item?.imageUrl ||
      item?.originalUrl ||
      item?.url ||
      item?.stored?.displayImage?.url ||
      item?.stored?.originalImage?.url
  );
}
function linkUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : "";
  } catch (_) {
    return "";
  }
}
function abortIfNeeded(signal) {
  if (signal?.aborted)
    throw new DOMException("PDF export cancelled.", "AbortError");
}
const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 0));

export function snapshotRegionGeometry(region) {
  if (!region || typeof region !== "object") return null;
  if (
    region.type === "polygon" &&
    arr(region.points).length >= 3 &&
    region.points.every(
      (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])
    )
  ) {
    const points = region.points.map(([x, y]) => [
      clamp(x, 0, 1),
      clamp(y, 0, 1),
    ]);
    const xs = points.map((p) => p[0]),
      ys = points.map((p) => p[1]);
    const box = {
      x: Math.min(...xs),
      y: Math.min(...ys),
      w: Math.max(...xs) - Math.min(...xs),
      h: Math.max(...ys) - Math.min(...ys),
    };
    return box.w > 0 && box.h > 0 ? { ...box, points } : null;
  }
  if (
    region.type === "box" &&
    [region.x, region.y, region.w, region.h].every(Number.isFinite) &&
    region.w > 0 &&
    region.h > 0
  ) {
    const x = clamp(region.x, 0, 1),
      y = clamp(region.y, 0, 1);
    const w = clamp(region.x + region.w, 0, 1) - x,
      h = clamp(region.y + region.h, 0, 1) - y;
    return w > 0 && h > 0 ? { x, y, w, h } : null;
  }
  return null;
}

export function buildDoorSnapshotData(input = {}, options = {}) {
  const scan = input.scan || {};
  const predictions = input.predictions || {};
  const extras = arr(
    own(input, "extras") ? input.extras : scan.supplemental
  ).map((item) => ({ ...item.stored, ...item }));
  const files = input.files || {};
  const sources = own(input, "sources")
    ? input.sources
    : Object.fromEntries(
        [
          ...Object.entries(scan.photos || {}),
          ...extras.map((item) => [item.id, item]),
        ].map(([id, item]) => [id, storedUrl(item)])
      );
  const rawChecklist = own(input, "checklist")
    ? input.checklist
    : scan.checklist?.sections;
  if (!Array.isArray(rawChecklist))
    throw new Error(
      "The current checklist must be supplied before exporting the PDF."
    );
  const parts = arr(input.parts);
  const grouped = arr(input.groupedSections);
  const groupNames = Object.fromEntries(
    parts.map((part) => [part.id, part.label])
  );
  const groupedById = new Map(grouped.map((section) => [section.id, section]));
  const classToGroup = new Map();
  parts.forEach((part) =>
    arr(part.classes).forEach((cls) => {
      if (!classToGroup.has(cls)) classToGroup.set(cls, part.id);
    })
  );
  const extraById = new Map(extras.map((item) => [item.id, item]));
  const labelClass = (cls) =>
    clean(input.classNames?.[cls] || human(cls) || "Unlabelled part");
  const observations = arr(
    own(input, "observations") ? input.observations : scan.observations
  ).map((item) => ({ ...item }));
  const hiddenLabels = new Set(
    input.hiddenCheckLabels || ["lock_configuration"]
  );
  const sections = rawChecklist.map((section, index) => {
    const detections = arr(groupedById.get(section.id)?.detections);
    const checks = arr(section.checks).map((check) => ({
      ...check,
      id: clean(check.id),
      label: hiddenLabels.has(check.id) ? "" : clean(check.label),
      status: stateOf(check.status),
      reason: clean(
        check.reason || "No explanation was recorded for this check."
      ),
      evidenceKeys: unique([
        ...arr(check.evidenceKeys),
        ...arr(check.attachedEvidenceKeys),
      ]),
      sourceLabel: SOURCE_NAMES[check.source] || human(check.source),
    }));
    return {
      id: section.id,
      number: index + 1,
      label: clean(
        section.label || groupNames[section.id] || human(section.id)
      ),
      status: stateOf(section.status),
      checks,
      identified: unique(
        detections.map((d) => clean(d.label || labelClass(d.region?.cls)))
      ),
      evidenceKeys: unique([
        ...arr(section.evidenceKeys),
        ...checks.flatMap((c) => c.evidenceKeys),
      ]),
      copy: clean(input.sectionCopy?.[section.id] || ""),
    };
  });
  const sectionById = new Map(sections.map((s) => [s.id, s]));
  const activeCoreKeys = own(input, "photoKeys")
    ? arr(input.photoKeys)
    : CORE.filter((id) => sources[id] || files[id] || scan.photos?.[id]);
  const keys = unique([
    ...activeCoreKeys,
    ...extras.map((e) => e.id),
    ...Object.keys(sources).filter(
      (id) => !CORE.includes(id) && !extraById.has(id)
    ),
  ]);
  const assets = keys.map((id, index) => {
    const item =
      extraById.get(id) || input.photoMetadata?.[id] || scan.photos?.[id] || {};
    const file = isBlob(files[id])
      ? files[id]
      : isBlob(item.file)
      ? item.file
      : null;
    const source =
      file ||
      mediaUrl(sources[id]) ||
      (!own(input, "sources") ? storedUrl(item) : "");
    const type =
      item.type ||
      (file?.type === "application/pdf"
        ? "document"
        : file?.type?.startsWith("video/")
        ? "video"
        : "photo");
    const prediction = predictions[id]?.prediction || predictions[id] || null;
    const contextGroup = id.startsWith("hinge_") ? "hinges" : item.group;
    const detections = grouped.flatMap((s) =>
      arr(s.detections)
        .filter((d) => d.side === id)
        .map((d) => ({ ...d, group: s.id }))
    );
    const detectionByIndex = new Map(
      detections.filter((d) => d.index >= 0).map((d) => [d.index, d])
    );
    const regions = arr(prediction?.regions)
      .map((region, regionIndex) => {
        const detected = detectionByIndex.get(regionIndex);
        const cls = String(region?.cls || "")
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, "_");
        const groupId =
          detected?.group || classToGroup.get(cls) || contextGroup || "other";
        const section = sectionById.get(groupId);
        return {
          geometry: snapshotRegionGeometry(region),
          groupId,
          groupLabel: section?.label || groupNames[groupId] || "Other",
          groupNumber: section?.number || null,
          status: section?.status || "review",
          classLabel: clean(detected?.label || labelClass(cls)),
          index: regionIndex,
        };
      })
      .filter((region) => region.geometry);
    const phase = input.statuses?.[id]?.phase;
    const saved = phase === "uploaded" || (!file && Boolean(storedUrl(item)));
    const available = Boolean(source || storedUrl(item));
    return {
      id,
      ref: `E${String(index + 1).padStart(2, "0")}`,
      type,
      source,
      label: clean(
        PHOTO_NAMES[id] || item.label || item.fileName || "Additional evidence"
      ),
      filename: clean(
        item.originalFile?.name || item.fileName || file?.name || ""
      ),
      groupId: contextGroup || (id === "certificate" ? "certificate" : "door"),
      groupLabel:
        groupNames[contextGroup] ||
        (id === "certificate" || contextGroup === "certificate"
          ? "Certificate"
          : "Door"),
      note: clean(item.note),
      checkId: item.checkId || item.questionId || "",
      capturedAt: item.capturedAt || item.capture?.capturedAt || null,
      available,
      saved,
      regions,
      analysisError: clean(
        input.statuses?.[id]?.error || item.analysisError || ""
      ),
      extractedText: clean(item.extractedText || ""),
      textResult: item.hingeTextResult || null,
      analysisStatus: clean(
        item.analysisStatus || (prediction ? "complete" : "not-available")
      ),
      hasPrediction: Boolean(prediction),
      detectedLabels: unique([
        ...arr(prediction?.regions).map((r) => labelClass(r?.cls)),
        ...Object.entries(prediction?.counts || {})
          .filter(([, count]) => Number(count) > 0)
          .map(([cls]) => labelClass(cls)),
      ]),
      link: options.includeEvidenceLinks
        ? linkUrl(storedUrl(item) || sources[id])
        : "",
      raster: null,
      loadError: "",
    };
  });
  const evidenceById = new Map(assets.map((a) => [a.id, a]));
  const counts = { pass: 0, fail: 0, review: 0, na: 0 };
  const checkCounts = { ...counts };
  sections.forEach((s) => {
    counts[s.status]++;
    s.checks.forEach((c) => checkCounts[c.status]++);
  });
  const generatedAt = dateValue(options.generatedAt) || new Date();
  const zone = options.timeZone || "Europe/London";
  const receipt = input.receipt;
  const confirmed = Boolean(receipt?.reference && receipt?.delivery);
  const dirty = Boolean(input.dirty);
  const reviewStatus = confirmed
    ? dirty
      ? "A prior review request was confirmed. The local changes included here are not part of that saved request."
      : receipt.delivery === "portal-queue"
      ? "Review request saved to the portal queue. This is not a completed review."
      : "Review request receipt confirmed. This is not a completed review."
    : "No review-request receipt is available in this session. Downloading this PDF does not submit the scan.";
  const questions = {
    escapeSide: "Which photo shows the inside / escape side?",
    ...input.questions,
  };
  const lastAnswers = new Map();
  observations.forEach((o, index) => {
    if (o.type === "checklist-answer" && o.questionId && !o.stale)
      lastAnswers.set(o.questionId, index);
  });
  const recorded = observations.map((o, index) => ({
    ...o,
    groupLabel: groupNames[o.group] || human(o.group),
    label: clean(
      questions[o.questionId] || human(o.questionId) || "Resident note"
    ),
    text: clean(o.text || o.note || ""),
    answer:
      {
        yes: "Yes",
        no: "No",
        unknown: "Not sure",
        front: "Front",
        back: "Back",
      }[o.answer] || clean(o.answer),
    superseded:
      o.type === "checklist-answer" &&
      (o.stale || lastAnswers.get(o.questionId) !== index),
  }));
  const data = {
    title: clean(options.title || "Door snapshot"),
    company: clean(options.companyName || "FireChecked"),
    companyContact: clean(options.companyContact || ""),
    preview: Boolean(options.preview),
    generatedAt,
    generatedText: dateText(generatedAt, zone),
    zone,
    scanId: clean(input.scanId || scan.id || scan.scanId || "Not saved yet"),
    doorId: clean(own(input, "doorId") ? input.doorId : scan.doorId),
    doorName: clean(input.doorName || scan.doorName || "Door scan"),
    revision: input.revision ?? scan.revision ?? null,
    savedAt: scan.updatedAt || null,
    savedText: dateText(scan.updatedAt, zone),
    checklistVersion: clean(
      input.checklistVersion || scan.checklist?.version || "Not recorded"
    ),
    contact:
      options.includeContact === false ? {} : { ...(input.contact || {}) },
    dirty,
    reviewStatus,
    receiptReference: confirmed ? clean(receipt.reference) : "",
    sections,
    counts,
    checkCounts,
    assets,
    evidenceById,
    observations: recorded,
    certificate: {
      ...(own(input, "certificateDecision")
        ? input.certificateDecision
        : scan.certification),
    },
    hinges: {
      ...(own(input, "hingeConfiguration")
        ? input.hingeConfiguration
        : scan.hinges),
    },
    includePhotoAppendix: options.includePhotoAppendix !== false,
    includeExtractedText: options.includeExtractedText !== false,
    warnings: [],
  };
  const totalText = JSON.stringify(data, (key, value) =>
    ["source", "raster"].includes(key) ? undefined : value
  );
  if (totalText.length > 2000000)
    throw new Error(
      "This report is too large. Export fewer notes or photos in one snapshot."
    );
  return data;
}

const FONT_METRICS = {
  normal: [
    556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
    556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
    556, 556, 278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278,
    333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278,
    584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500,
    667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667,
    611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556,
    222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722,
    500, 500, 500, 334, 260, 334, 584, 350, 556, 350, 222, 556, 333, 1000, 556,
    556, 333, 1000, 667, 333, 1000, 350, 611, 350, 350, 222, 222, 333, 333, 350,
    556, 1000, 333, 1000, 500, 333, 944, 350, 500, 667, 278, 333, 556, 556, 556,
    556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333,
    333, 556, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611, 667, 667, 667,
    667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722,
    778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611, 556,
    556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278,
    556, 556, 556, 556, 556, 556, 556, 584, 611, 556, 556, 556, 556, 500, 556,
    500,
  ],
  bold: [
    556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
    556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
    556, 556, 278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278,
    333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333,
    584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556,
    722, 611, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667,
    611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611, 556, 333, 611, 611,
    278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778,
    556, 556, 500, 389, 280, 389, 584, 350, 556, 350, 278, 556, 500, 1000, 556,
    556, 333, 1000, 667, 333, 1000, 350, 611, 350, 350, 278, 278, 500, 500, 350,
    556, 1000, 333, 1000, 556, 333, 944, 350, 500, 667, 278, 333, 556, 556, 556,
    556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333,
    333, 611, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611, 722, 722, 722,
    722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722,
    778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611, 556,
    556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278,
    611, 611, 611, 611, 611, 611, 611, 584, 611, 611, 611, 611, 611, 556, 611,
    556,
  ],
};
const CP_EXTRA = {
  8364: 128,
  8218: 130,
  402: 131,
  8222: 132,
  8230: 133,
  8224: 134,
  8225: 135,
  710: 136,
  8240: 137,
  352: 138,
  8249: 139,
  338: 140,
  381: 142,
  8216: 145,
  8217: 146,
  8220: 147,
  8221: 148,
  8226: 149,
  8211: 150,
  8212: 151,
  732: 152,
  8482: 153,
  353: 154,
  8250: 155,
  339: 156,
  382: 158,
  376: 159,
};
const ascii = (s) => new TextEncoder().encode(s);
const number = (v) => Number(v.toFixed(3)).toString();
const rgb = (hex) =>
  [0, 2, 4]
    .map((i) => number(parseInt(hex.slice(i, i + 2), 16) / 255))
    .join(" ");
function cpBytes(text) {
  const out = [];
  for (const c of String(text)) {
    const point = c.codePointAt(0);
    if ((point >= 32 && point <= 126) || (point >= 160 && point <= 255))
      out.push(point);
    else if (own(CP_EXTRA, point)) out.push(CP_EXTRA[point]);
    else return null;
  }
  return out;
}
const hexBytes = (bytes) =>
  `<${[...bytes].map((v) => v.toString(16).padStart(2, "0")).join("")}>`;
const utf16 = (text) =>
  `<feff${[...String(text).split("")]
    .map((c) => c.charCodeAt(0).toString(16).padStart(4, "0"))
    .join("")}>`;
function base64Bytes(dataUrl) {
  const binary = atob(dataUrl.slice(dataUrl.indexOf(",") + 1));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}
let textCanvas;
function measureContext() {
  if (!textCanvas) textCanvas = document.createElement("canvas");
  return textCanvas.getContext("2d");
}
function widthOf(text, size = 10, bold = false) {
  const bytes = cpBytes(text);
  if (bytes)
    return (
      (bytes.reduce(
        (sum, code) => sum + FONT_METRICS[bold ? "bold" : "normal"][code],
        0
      ) *
        size) /
      1000
    );
  const ctx = measureContext();
  ctx.font = `${bold ? "700" : "400"} ${size}px Arial, sans-serif`;
  return ctx.measureText(text).width;
}
function wrapText(value, maxWidth, size = 10, bold = false) {
  const text = clean(value),
    lines = [];
  for (const paragraph of text.split("\n")) {
    if (!paragraph) {
      lines.push("");
      continue;
    }
    let line = "";
    for (const token of paragraph.split(/\s+/)) {
      if (widthOf(line ? `${line} ${token}` : token, size, bold) <= maxWidth) {
        line = line ? `${line} ${token}` : token;
        continue;
      }
      if (line) {
        lines.push(line);
        line = "";
      }
      if (widthOf(token, size, bold) <= maxWidth) {
        line = token;
        continue;
      }
      for (const char of token) {
        if (line && widthOf(line + char, size, bold) > maxWidth) {
          lines.push(line);
          line = "";
        }
        line += char;
      }
    }
    if (line) lines.push(line);
  }
  return lines.length ? lines : [""];
}
class SnapshotPdfWriter {
  constructor() {
    this.pages = [];
    this.images = [];
    this.imageMap = new Map();
    this.audit = [];
    this.addPage();
  }
  addPage() {
    if (this.pages.length >= 120)
      throw new Error(
        "The snapshot exceeds 120 pages. Reduce the notes or photo appendix."
      );
    this.page = { commands: [], images: new Set(), links: [] };
    this.pages.push(this.page);
  }
  command(s) {
    this.page.commands.push(s);
  }
  rect(x, y, w, h, fill = C.pale, stroke = null, radius = 0) {
    const top = PAGE.height - y,
      bottom = top - h;
    this.command(
      `q ${rgb(fill || C.white)} rg ${rgb(stroke || fill || C.white)} RG 0.6 w`
    );
    if (radius) {
      const r = Math.min(radius, w / 2, h / 2),
        k = r * 0.55228475;
      this.command(
        `${number(x + r)} ${number(bottom)} m ${number(x + w - r)} ${number(
          bottom
        )} l ` +
          `${number(x + w - r + k)} ${number(bottom)} ${number(x + w)} ${number(
            bottom + r - k
          )} ${number(x + w)} ${number(bottom + r)} c ` +
          `${number(x + w)} ${number(top - r)} l ${number(x + w)} ${number(
            top - r + k
          )} ${number(x + w - r + k)} ${number(top)} ${number(
            x + w - r
          )} ${number(top)} c ` +
          `${number(x + r)} ${number(top)} l ${number(x + r - k)} ${number(
            top
          )} ${number(x)} ${number(top - r + k)} ${number(x)} ${number(
            top - r
          )} c ` +
          `${number(x)} ${number(bottom + r)} l ${number(x)} ${number(
            bottom + r - k
          )} ${number(x + r - k)} ${number(bottom)} ${number(x + r)} ${number(
            bottom
          )} c h ${fill && stroke ? "B" : fill ? "f" : "S"} Q`
      );
    } else
      this.command(
        `${number(x)} ${number(bottom)} ${number(w)} ${number(h)} re ${
          fill && stroke ? "B" : fill ? "f" : "S"
        } Q`
      );
  }
  line(x1, y1, x2, y2, colour = C.line, weight = 0.6) {
    this.command(
      `q ${rgb(colour)} RG ${number(weight)} w ${number(x1)} ${number(
        PAGE.height - y1
      )} m ${number(x2)} ${number(PAGE.height - y2)} l S Q`
    );
  }
  polygon(points, colour, weight = 0.8) {
    this.command(
      `q ${rgb(colour)} RG ${number(weight)} w ${points
        .map(
          ([x, y], i) =>
            `${number(x)} ${number(PAGE.height - y)} ${i ? "l" : "m"}`
        )
        .join(" ")} h S Q`
    );
  }
  image(raster, x, y, w, h, actualText = "") {
    let id = this.imageMap.get(raster);
    if (!id) {
      id = this.images.length + 1;
      this.images.push(raster);
      this.imageMap.set(raster, id);
    }
    this.page.images.add(id);
    if (actualText)
      this.command(`/Span << /ActualText ${utf16(actualText)} >> BDC`);
    this.command(
      `q ${number(w)} 0 0 ${number(h)} ${number(x)} ${number(
        PAGE.height - y - h
      )} cm /Im${id} Do Q`
    );
    if (actualText) {
      this.command(
        `BT /F1 8 Tf 3 Tr 1 0 0 1 ${number(x)} ${number(
          PAGE.height - y - h
        )} Tm <20> Tj 0 Tr ET EMC`
      );
    }
  }
  text(
    text,
    x,
    baseline,
    size = 10,
    colour = C.ink,
    bold = false,
    align = "left"
  ) {
    text = clean(text);
    if (!text) return;
    const width = widthOf(text, size, bold);
    if (align === "right") x -= width;
    if (align === "center") x -= width / 2;
    this.audit.push({
      page: this.pages.indexOf(this.page) + 1,
      text,
      x,
      baseline,
      width,
      size,
    });
    const bytes = cpBytes(text);
    if (bytes) {
      this.command(
        `BT /${bold ? "F2" : "F1"} ${number(size)} Tf ${rgb(
          colour
        )} rg 1 0 0 1 ${number(x)} ${number(
          PAGE.height - baseline
        )} Tm ${hexBytes(bytes)} Tj ET`
      );
      return;
    }
    const scale = 3,
      canvas = document.createElement("canvas");
    const h = size * 1.7;
    canvas.width = Math.max(1, Math.ceil(width * scale));
    canvas.height = Math.ceil(h * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.font = `${bold ? "700" : "400"} ${size}px Arial, sans-serif`;
    ctx.fillStyle = `#${colour}`;
    ctx.fillText(text, 0, size * 1.12);
    this.image(
      {
        bytes: base64Bytes(canvas.toDataURL("image/jpeg", 0.94)),
        width: canvas.width,
        height: canvas.height,
      },
      x,
      baseline - size * 1.12,
      width,
      h,
      text
    );
    canvas.width = canvas.height = 0;
  }
  link(url, x, y, w, h) {
    if (linkUrl(url)) this.page.links.push({ url, x, y, w, h });
  }
  toBlob(title, company, createdAt) {
    const objects = [null],
      reserve = () => (objects.push(null), objects.length - 1);
    const catalog = reserve(),
      pagesId = reserve(),
      f1 = reserve(),
      f2 = reserve(),
      info = reserve();
    const imageIds = this.images.map(() => reserve());
    const pages = this.pages.map((page) => ({
      page,
      id: reserve(),
      contentId: reserve(),
      linkIds: page.links.map(() => reserve()),
    }));
    const textObject = (id, content) => {
      objects[id] = ascii(content);
    };
    const stream = (id, dictionary, bytes) => {
      objects[id] = [
        ascii(`<< ${dictionary} /Length ${bytes.length} >>\nstream\n`),
        bytes,
        ascii("\nendstream"),
      ];
    };
    textObject(
      catalog,
      `<< /Type /Catalog /Pages ${pagesId} 0 R /Lang (en-GB) >>`
    );
    textObject(
      pagesId,
      `<< /Type /Pages /Count ${pages.length} /Kids [${pages
        .map((p) => `${p.id} 0 R`)
        .join(" ")}] >>`
    );
    textObject(
      f1,
      "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>"
    );
    textObject(
      f2,
      "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>"
    );
    const date = createdAt
      .toISOString()
      .replace(/[-:TZ.]/g, "")
      .slice(0, 14);
    textObject(
      info,
      `<< /Title ${utf16(title)} /Author ${utf16(company)} /Subject ${utf16(
        "Preliminary photo and evidence checks. Not an inspection certificate."
      )} /Creator (FireChecked.co.uk) /CreationDate (D:${date}Z) >>`
    );
    this.images.forEach((im, i) =>
      stream(
        imageIds[i],
        `/Type /XObject /Subtype /Image /Width ${im.width} /Height ${im.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode`,
        im.bytes
      )
    );
    pages.forEach(({ page, id, contentId, linkIds }) => {
      stream(contentId, "", ascii(page.commands.join("\n")));
      page.links.forEach((link, index) =>
        textObject(
          linkIds[index],
          `<< /Type /Annot /Subtype /Link /Border [0 0 0] /Rect [${number(
            link.x
          )} ${number(PAGE.height - link.y - link.h)} ${number(
            link.x + link.w
          )} ${number(PAGE.height - link.y)}] /A << /S /URI /URI ${hexBytes(
            ascii(link.url)
          )} >> >>`
        )
      );
      textObject(
        id,
        `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE.width} ${
          PAGE.height
        }] /Resources << /Font << /F1 ${f1} 0 R /F2 ${f2} 0 R >> /XObject << ${[
          ...page.images,
        ]
          .map((i) => `/Im${i} ${imageIds[i - 1]} 0 R`)
          .join(" ")} >> >> /Contents ${contentId} 0 R ${
          linkIds.length
            ? `/Annots [${linkIds.map((i) => `${i} 0 R`).join(" ")}]`
            : ""
        } >>`
      );
    });
    const chunks = [ascii("%PDF-1.4\n%FireChecked\n")],
      offsets = [0];
    let offset = chunks[0].length;
    for (let i = 1; i < objects.length; i++) {
      offsets[i] = offset;
      const pieces = [
        ascii(`${i} 0 obj\n`),
        ...(Array.isArray(objects[i]) ? objects[i] : [objects[i]]),
        ascii("\nendobj\n"),
      ];
      pieces.forEach((piece) => {
        chunks.push(piece);
        offset += piece.length;
      });
    }
    chunks.push(
      ascii(
        `xref\n0 ${objects.length}\n0000000000 65535 f \n${offsets
          .slice(1)
          .map((v) => `${String(v).padStart(10, "0")} 00000 n \n`)
          .join("")}trailer\n<< /Size ${
          objects.length
        } /Root ${catalog} 0 R /Info ${info} 0 R >>\nstartxref\n${offset}\n%%EOF\n`
      )
    );
    return new Blob(chunks, { type: "application/pdf" });
  }
}

async function rasterizePhoto(
  asset,
  { signal, timeout = 18000, maxEdge = 1600 } = {}
) {
  abortIfNeeded(signal);
  if (!asset.source) throw new Error("No readable image source is available.");
  timeout = Number.isFinite(Number(timeout))
    ? clamp(Number(timeout), 1000, 60000)
    : 18000;
  maxEdge = Number.isFinite(Number(maxEdge))
    ? clamp(Number(maxEdge), 640, 2560)
    : 1600;
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal?.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(cancel, timeout);
  let objectUrl = "",
    image = null;
  try {
    let blob = asset.source;
    if (!isBlob(blob)) {
      const response = await fetch(blob, {
        signal: controller.signal,
        mode: "cors",
        credentials: "omit",
        referrerPolicy: "no-referrer",
      });
      if (!response.ok)
        throw new Error(`Photo could not be read (HTTP ${response.status}).`);
      if (Number(response.headers.get("content-length")) > 25 * 1024 * 1024)
        throw new Error("The image exceeds the 25 MB export limit.");
      blob = await response.blob();
    }
    if (blob.size > 25 * 1024 * 1024)
      throw new Error("The image exceeds the 25 MB export limit.");
    if (!blob.size) throw new Error("The image file is empty.");
    if (blob.type && !/^image\/(jpeg|png|webp|heic|heif)$/i.test(blob.type))
      throw new Error("This source is not a supported photo.");
    abortIfNeeded(signal);
    abortIfNeeded(controller.signal);
    objectUrl = URL.createObjectURL(blob);
    image = new Image();
    const imageReady = new Promise((resolve, reject) => {
      const onAbort = () =>
        reject(
          new DOMException(
            "Photo loading timed out or was cancelled.",
            "AbortError"
          )
        );
      controller.signal.addEventListener("abort", onAbort, { once: true });
      image.onload = () => {
        controller.signal.removeEventListener("abort", onAbort);
        resolve();
      };
      image.onerror = () => {
        controller.signal.removeEventListener("abort", onAbort);
        reject(new Error("The browser could not decode this photo."));
      };
      image.src = objectUrl;
    });
    await imageReady;
    abortIfNeeded(signal);
    if (
      !image.naturalWidth ||
      !image.naturalHeight ||
      image.naturalWidth * image.naturalHeight > 60000000
    )
      throw new Error(
        "The image dimensions are invalid or too large for this export."
      );
    const scale = Math.min(
      1,
      clamp(maxEdge, 640, 2560) /
        Math.max(image.naturalWidth, image.naturalHeight)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    try {
      const ctx = canvas.getContext("2d");
      if (!ctx)
        throw new Error("Photo processing is unavailable in this browser.");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      return {
        bytes: base64Bytes(canvas.toDataURL("image/jpeg", 0.88)),
        width: canvas.width,
        height: canvas.height,
      };
    } finally {
      canvas.width = canvas.height = 0;
    }
  } catch (error) {
    abortIfNeeded(signal);
    if (controller.signal.aborted)
      throw new Error("Photo loading timed out; it was not embedded.");
    if (error instanceof TypeError)
      throw new Error(
        "Photo unavailable: check the connection and the storage CORS policy."
      );
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
    if (image) {
      image.onload = image.onerror = null;
      image.src = "";
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
function compactLines(text, width, size, maxLines = 2, bold = false) {
  const lines = wrapText(text, width, size, bold);
  if (lines.length <= maxLines) return lines;
  const visible = lines.slice(0, maxLines);
  let last = visible[maxLines - 1];
  while (last && widthOf(last + "...", size, bold) > width)
    last = last.slice(0, -1);
  visible[maxLines - 1] = last.trimEnd() + "...";
  return visible;
}
class ReportLayout {
  constructor(data) {
    this.data = data;
    this.pdf = new SnapshotPdfWriter();
    this.y = PAGE.top;
    this.width = PAGE.width - PAGE.margin * 2;
    this.section = null;
    this.header("Your door / initial photo review");
  }
  header(subtitle) {
    const p = this.pdf,
      data = this.data;
    let brandSize = 20;
    while (brandSize > 11 && widthOf(data.company, brandSize, true) > 315)
      brandSize--;
    p.text(
      compactLines(data.company, 315, brandSize, 1, true)[0],
      PAGE.margin,
      43,
      brandSize,
      C.orange,
      true
    );
    p.text(subtitle, PAGE.margin, 60, 9, C.muted);
    const badge = data.preview ? "PREVIEW / TEST DATA" : "PHOTO REVIEW";
    const badgeW = widthOf(badge, 8, true) + 20;
    p.rect(
      PAGE.width - PAGE.margin - badgeW,
      29,
      badgeW,
      21,
      C.reviewBg,
      null,
      4
    );
    p.text(
      badge,
      PAGE.width - PAGE.margin - badgeW + 10,
      42.5,
      8,
      C.review,
      true
    );
    p.line(PAGE.margin, 72, PAGE.width - PAGE.margin, 72);
  }
  page(subtitle = "Photo review / continued", continuing = null) {
    this.pdf.addPage();
    this.y = PAGE.top;
    this.header(subtitle);
    if (continuing) this.band(continuing, true);
  }
  ensure(height, continuing = this.section) {
    if (this.y + height > PAGE.bottom)
      this.page(
        continuing
          ? "The complete breakdown / continued"
          : "Photo review / continued",
        continuing
      );
  }
  paragraph(
    text,
    {
      size = 9.7,
      colour = C.muted,
      bold = false,
      x = PAGE.margin,
      width = this.width,
      leading = size * 1.35,
      gap = 8,
      continuing = this.section,
    } = {}
  ) {
    const lines = wrapText(text, width, size, bold);
    for (const line of lines) {
      this.ensure(leading + 1, continuing);
      this.pdf.text(line, x, this.y + size, size, colour, bold);
      this.y += leading;
    }
    this.y += gap;
  }
  heading(text, size = 22) {
    this.ensure(size * 1.4 + 20, null);
    this.paragraph(text, {
      size,
      colour: C.ink,
      bold: true,
      gap: 13,
      leading: size * 1.15,
      continuing: null,
    });
  }
  band(section, continued = false) {
    const label = `${section.number}. ${section.label}${
      continued ? " / continued" : ""
    }`;
    const lines = wrapText(label, this.width - 175, 12, true);
    const height = Math.max(30, lines.length * 14 + 14);
    this.pdf.rect(PAGE.margin, this.y, this.width, height, C.pale, null, 7);
    lines.forEach((line, i) =>
      this.pdf.text(
        line,
        PAGE.margin + 11,
        this.y + 20 + i * 14,
        12,
        C.ink,
        true
      )
    );
    this.pdf.text(
      STATUS[section.status].label,
      PAGE.width - PAGE.margin - 11,
      this.y + 19,
      8.6,
      STATUS[section.status].colour,
      true,
      "right"
    );
    this.y += height + 12;
  }
  footer() {
    const total = this.pdf.pages.length;
    this.pdf.pages.forEach((page, index) => {
      this.pdf.page = page;
      this.pdf.line(PAGE.margin, 801, PAGE.width - PAGE.margin, 801);
      const text = `${this.data.company} | ${
        this.data.preview ? "Test-data preview" : "Private door snapshot"
      }`;
      this.pdf.text(
        compactLines(text, 320, 8, 1)[0],
        PAGE.margin,
        817,
        8,
        C.muted
      );
      this.pdf.text(
        `${index + 1} / ${total}`,
        PAGE.width - PAGE.margin,
        817,
        8,
        C.muted,
        false,
        "right"
      );
    });
  }
}

function drawPhoto(pdf, asset, x, y, width, height) {
  pdf.rect(x, y, width, height, "EEF3ED", null, 8);
  if (!asset?.raster) {
    pdf.text(
      asset?.loadError ? "Photo unavailable in this PDF" : "No photo supplied",
      x + width / 2,
      y + height / 2 - 8,
      10,
      C.muted,
      true,
      "center"
    );
    const explanation =
      asset?.loadError || "This item remains available to add in the app.";
    compactLines(explanation, width - 28, 8, 3).forEach((line, i) =>
      pdf.text(
        line,
        x + width / 2,
        y + height / 2 + 10 + i * 11,
        8,
        C.muted,
        false,
        "center"
      )
    );
    return;
  }
  const im = asset.raster;
  const scale = Math.min((width - 12) / im.width, (height - 12) / im.height);
  const w = im.width * scale,
    h = im.height * scale,
    left = x + (width - w) / 2,
    top = y + (height - h) / 2;
  pdf.image(im, left, top, w, h, asset.label);

  const textWidth = Math.max(28, width - 10);
  const font = 7;
  const lineHeight = 8.5;
  const labels = asset.regions.map((r) => {
    const g = r.geometry;
    const colour = STATUS[r.status].colour;
    if (g.points) {
      pdf.polygon(
        g.points.map(([u, v]) => [left + u * w, top + v * h]),
        colour,
        0.8
      );
    } else {
      pdf.rect(
        left + g.x * w,
        top + g.y * h,
        g.w * w,
        g.h * h,
        null,
        colour,
        0
      );
    }
    const text = `${r.groupNumber ? `${r.groupNumber}. ` : ""}${
      r.classLabel || r.groupLabel
    }`;
    const lines = compactLines(text, textWidth, font, 2, true);
    return {
      region: r,
      anchorX: left + (g.x + Math.min(g.w || 0, 0.5)) * w,
      anchorY: top + g.y * h,
      lines,
      colour,
      height: lines.length * lineHeight,
    };
  });

  const placed = [];
  labels
    .sort((a, b) => a.anchorY - b.anchorY)
    .forEach((item) => {
      const labelW = Math.min(
        textWidth,
        Math.max(...item.lines.map((line) => widthOf(line, font, true))) + 2
      );
      let tx = clamp(item.anchorX + 5, x + 5, x + width - labelW - 5);
      let ty = clamp(
        item.anchorY - item.height - 2,
        y + 4,
        y + height - item.height - 4
      );

      const overlaps = (px, py) =>
        placed.some(
          (b) =>
            px < b.x + b.w + 3 &&
            px + labelW > b.x - 3 &&
            py < b.y + b.h + 3 &&
            py + item.height > b.y - 3
        );

      const candidates = [
        [tx, ty],
        [tx, clamp(item.anchorY + 5, y + 4, y + height - item.height - 4)],
        [clamp(item.anchorX - labelW - 5, x + 5, x + width - labelW - 5), ty],
        [
          clamp(item.anchorX - labelW - 5, x + 5, x + width - labelW - 5),
          clamp(item.anchorY + 5, y + 4, y + height - item.height - 4),
        ],
      ];
      for (const [cx, cy] of candidates) {
        if (!overlaps(cx, cy)) {
          tx = cx;
          ty = cy;
          break;
        }
      }

      const labelRight = tx + labelW;
      const labelBottom = ty + item.height;
      if (labelRight < x + width || tx > x) {
        pdf.line(
          item.anchorX,
          item.anchorY,
          clamp(item.anchorX, tx, labelRight),
          clamp(item.anchorY, ty, labelBottom),
          item.colour,
          0.5
        );
      }
      placed.push({ x: tx, y: ty, w: labelW, h: item.height });
      const labelColour =
        item.region.status === "review" ? C.orange : item.colour;
      item.lines.forEach((line, index) => {
        const baseline = ty + font + index * lineHeight;
        const outlineOffsets = [
          [-0.45, 0],
          [0.45, 0],
          [0, -0.45],
          [0, 0.45],
          [-0.35, -0.35],
          [0.35, -0.35],
          [-0.35, 0.35],
          [0.35, 0.35],
        ];
        outlineOffsets.forEach(([dx, dy]) =>
          pdf.text(line, tx + dx, baseline + dy, font, labelColour, true)
        );
        pdf.text(line, tx, baseline, font, C.white, true);
      });
    });
}

function drawCover(layout) {
  const d = layout.data,
    p = layout.pdf,
    m = PAGE.margin,
    w = layout.width;
  layout.heading(d.title, 27);
  const sub = d.preview
    ? `${d.doorName} - layout test, not a client assessment.`
    : d.doorName;
  compactLines(sub, w, 11, 2).forEach((line) => {
    p.text(line, m, layout.y + 11, 11, C.muted);
    layout.y += 14;
  });
  layout.y += 7;
  p.text(`Prepared ${d.generatedText}`, m, layout.y + 8, 8.4, C.muted);
  layout.y += 15;
  const status = d.dirty
    ? "Local changes included - not uploaded"
    : "Snapshot of the loaded scan";
  p.text(status, m, layout.y + 8, 8.4, d.dirty ? C.review : C.muted);
  layout.y += 24;
  const tileW = (w - 24) / 3;
  [
    ["pass", "Fine", C.passBg],
    ["fail", "Attention", C.failBg],
    ["review", "Review", C.reviewBg],
  ].forEach(([key, label, bg], i) => {
    const x = m + i * (tileW + 12);
    p.rect(x, layout.y, tileW, 35, bg, null, 7);
    p.text(
      `${d.counts[key]}  ${label}`,
      x + 12,
      layout.y + 23,
      12,
      C.ink,
      true
    );
  });
  layout.y += 49;
  p.text(
    `Counts are sections, not detections. ${d.counts.na} not applicable.`,
    m,
    layout.y,
    8.4,
    C.muted
  );
  layout.y += 19;
  const pw = (w - 22) / 2,
    py = layout.y + 10,
    ph = 248;
  ["front", "back"].forEach((side, i) => {
    const asset = d.assets.find((a) => a.id === side),
      x = m + i * (pw + 22);
    p.text(side.toUpperCase(), x, layout.y, 9, C.muted, true);
    drawPhoto(p, asset, x, py, pw, ph);
  });
  layout.y = py + ph + 16;
  layout.paragraph(
    "Outlines identify model-returned parts. Colours refer to the recorded section result, not model confidence or measured dimensions.",
    { size: 8.1, leading: 10.5, gap: 12 }
  );
  layout.paragraph("What happens next", {
    size: 14.5,
    colour: C.ink,
    bold: true,
    gap: 8,
  });
  const actions = d.sections
    .filter((s) => s.status === "fail" || s.status === "review")
    .sort(
      (a, b) => (a.status === "fail" ? 0 : 1) - (b.status === "fail" ? 0 : 1)
    );
  const shown = actions.slice(0, 3);
  if (!shown.length)
    layout.paragraph(
      "No failed or unresolved sections are recorded in this snapshot. This does not certify the door.",
      { size: 9.7 }
    );
  shown.forEach((section) => {
    const check =
      section.checks.find((c) => c.status === "fail") ||
      section.checks.find((c) => c.status === "review");
    const text = `${section.label} - ${
      check?.reason || STATUS[section.status].label
    }`;
    compactLines(text, w, 9.5, 2).forEach((line) => {
      p.text(line, m, layout.y + 9.5, 9.5, C.ink);
      layout.y += 12;
    });
    layout.y += 4;
  });
  if (actions.length > shown.length) {
    p.text(
      `${
        actions.length - shown.length
      } further sections need attention or review. Full details follow.`,
      m,
      layout.y + 8,
      8,
      C.muted
    );
    layout.y += 17;
  }
  const message =
    "Add any missing evidence, then send one review pack from the app.";
  if (layout.y + 65 <= PAGE.bottom) {
    p.rect(m, layout.y, w, 35, C.pale, null, 7);
    p.text(message, m + 12, layout.y + 22, 10, C.green, true);
    layout.y += 47;
  }
  layout.paragraph(
    "Not an inspection certificate. Missing evidence stays unresolved. Only the checks explicitly recorded in this snapshot are covered.",
    { size: 8.3, leading: 10.7, gap: 0, continuing: null }
  );
}
function drawCheck(layout, section, check) {
  const p = layout.pdf,
    m = PAGE.margin + 9,
    w = layout.width - 18;
  layout.ensure(52, section);
  const status = STATUS[check.status];
  const source = check.sourceLabel;
  if (check.label) {
    const lines = wrapText(check.label, w - 74, 10.4, true);
    p.text(
      status.tag,
      PAGE.width - PAGE.margin - 9,
      layout.y + 10.4,
      8.4,
      status.colour,
      true,
      "right"
    );
    layout.paragraph(check.label, {
      x: m,
      width: w - 74,
      size: 10.4,
      colour: C.green,
      bold: true,
      gap: 4,
      leading: 13.5,
    });
  } else {
    p.text(
      status.tag,
      PAGE.width - PAGE.margin - 9,
      layout.y + 10,
      8.4,
      status.colour,
      true,
      "right"
    );
  }
  layout.paragraph(check.reason, {
    x: m,
    width: check.label ? w : w - 75,
    size: 9.6,
    colour: C.ink,
    gap: 4,
  });
  if (source)
    layout.paragraph(`Basis: ${source}`, {
      x: m,
      width: w,
      size: 8,
      colour: C.muted,
      gap: 4,
      leading: 10.5,
    });
  const refs = check.evidenceKeys
    .map((key) => layout.data.evidenceById.get(key)?.ref)
    .filter(Boolean);
  if (refs.length)
    layout.paragraph(
      `Evidence: ${unique(refs).join(", ")}${
        check.requiredMedia && check.evidenceProvided
          ? " - attached, not automatically approved"
          : ""
      }`,
      { x: m, width: w, size: 8, colour: C.muted, gap: 5, leading: 10.5 }
    );
  layout.y += 5;
}
function drawBreakdown(layout) {
  layout.page("AI breakdown / stated checks and their limits");
  layout.heading("The complete breakdown");
  const d = layout.data;
  layout.paragraph(
    "Fine means passed for the stated checks only. Attention means at least one recorded failure. Review means an unresolved check. These are the app's recorded results, not a whole-door compliance decision.",
    { size: 9.2, gap: 17 }
  );
  d.sections.forEach((section) => {
    const identification = section.identified.length
      ? `Identified parts: ${section.identified.join(", ")}`
      : "No parts identified for this group in the available model output. This is not proof of absence.";
    const openingHeight =
      42 +
      (section.copy
        ? wrapText(section.copy, layout.width, 9.4).length * 12.69 + 7
        : 0) +
      wrapText(identification, layout.width, 8.6).length * 11.61 +
      10 +
      68;
    layout.ensure(Math.min(openingHeight, 400), null);
    layout.section = section;
    layout.band(section);
    if (section.copy)
      layout.paragraph(section.copy, { size: 9.4, colour: C.ink, gap: 7 });
    layout.paragraph(identification, { size: 8.6, gap: 10 });
    if (!section.checks.length)
      layout.paragraph(
        "No checks were supplied for this section; it remains unresolved."
      );
    section.checks.forEach((check) => drawCheck(layout, section, check));
    layout.y += 14;
    layout.section = null;
  });
}
function drawPhotoAppendix(layout) {
  const d = layout.data;
  const photos = d.assets.filter(
    (a) => a.type === "photo" && !["front", "back"].includes(a.id)
  );
  if (!d.includePhotoAppendix || !photos.length) return;
  if (layout.y + 385 > PAGE.bottom)
    layout.page("Photographs / supporting evidence");
  layout.heading("Photographs & evidence");
  layout.paragraph(
    "Front and back photographs are on the snapshot page. The remaining supplied photographs are below, including hinge close-ups and certificates. Image outlines are model identifications, not measurements.",
    { size: 9.3, gap: 17 }
  );
  const pw = (layout.width - 22) / 2,
    ph = 190;
  for (let i = 0; i < photos.length; i += 2) {
    if (layout.y + 275 > PAGE.bottom) {
      layout.page("Photographs / continued");
    }
    const rowY = layout.y;
    photos.slice(i, i + 2).forEach((asset, j) => {
      const x = PAGE.margin + j * (pw + 22);
      const heading = compactLines(
        `${asset.ref}  ${asset.label}`,
        pw,
        10,
        2,
        true
      );
      heading.forEach((line, k) =>
        layout.pdf.text(line, x, rowY + 10 + k * 13, 10, C.ink, true)
      );
      drawPhoto(layout.pdf, asset, x, rowY + 32, pw, ph);
      const description =
        asset.id === "certificate" || asset.groupId === "certificate"
          ? "Certificate evidence. Any extracted rating remains a text-screening result, not independently verified certification."
          : asset.regions.length
          ? `${asset.regions.length} outline${
              asset.regions.length === 1 ? "" : "s"
            } shown. ${asset.detectedLabels.join(", ")}`
          : asset.detectedLabels.length
          ? `Identified without drawable coordinates: ${asset.detectedLabels.join(
              ", "
            )}`
          : "No model annotations available for this photograph.";
      compactLines(description, pw, 8.5, 3).forEach((line, k) =>
        layout.pdf.text(line, x, rowY + ph + 45 + k * 11, 8.5, C.muted)
      );
    });
    layout.y = rowY + 270;
  }
}
function drawEvidenceRegister(layout) {
  layout.section = null;
  if (layout.y + 130 > PAGE.bottom)
    layout.page("Evidence register / supplied files");
  layout.heading("Evidence register", 20);
  const d = layout.data;
  layout.paragraph(
    "Evidence being present does not mean it has been approved. Videos and PDF documents are listed, not played or merged into this snapshot. Local-only files must still be uploaded in the app.",
    { size: 9.1, gap: 13 }
  );
  if (!d.assets.length)
    layout.paragraph("No photo, video or document evidence was supplied.");
  d.assets.forEach((asset) => {
    layout.ensure(76, null);
    layout.paragraph(`${asset.ref}  ${asset.label}`, {
      size: 10.5,
      colour: C.ink,
      bold: true,
      gap: 5,
    });
    const condition = asset.saved
      ? "Saved file"
      : asset.available
      ? "On this device / not confirmed uploaded"
      : "File unavailable";
    layout.paragraph(
      `${human(asset.type)} | ${asset.groupLabel} | ${condition}`,
      { size: 8.9, gap: 5 }
    );
    if (asset.filename && asset.filename !== asset.label)
      layout.paragraph(`File: ${asset.filename}`, { size: 8.4, gap: 4 });
    if (asset.capturedAt)
      layout.paragraph(`Captured: ${dateText(asset.capturedAt, d.zone)}`, {
        size: 8.4,
        gap: 4,
      });
    if (asset.loadError)
      layout.paragraph(`Export warning: ${asset.loadError}`, {
        size: 9,
        colour: C.fail,
        gap: 5,
      });
    if (asset.analysisError)
      layout.paragraph(`Analysis warning: ${asset.analysisError}`, {
        size: 9,
        colour: C.review,
        gap: 5,
      });
    if (asset.note)
      layout.paragraph(`Recorded note: ${asset.note}`, {
        size: 9.1,
        colour: C.ink,
        gap: 6,
      });
    if (asset.link) {
      layout.ensure(18, null);
      const label = "Open saved evidence (requires permitted access)";
      layout.pdf.text(label, PAGE.margin, layout.y + 9, 9, C.green);
      layout.pdf.link(
        asset.link,
        PAGE.margin,
        layout.y,
        Math.min(layout.width, widthOf(label, 9)),
        13
      );
      layout.y += 20;
    }
    layout.y += 8;
  });
}
function drawRecords(layout) {
  const d = layout.data;
  if (layout.y + 210 > PAGE.bottom)
    layout.page("Recorded answers / scan details");
  layout.heading("Photographs & recorded answers");
  const recordLine = (label, value) => {
    if (value != null && value !== "")
      layout.paragraph(`${label}: ${clean(value)}`, { size: 9.5, gap: 7 });
  };
  recordLine("Door", d.doorName);
  recordLine("Door reference", d.doorId);
  recordLine("Scan reference", d.scanId);
  recordLine("Revision", d.revision);
  recordLine("Prepared", d.generatedText);
  recordLine("Last saved", d.savedText);
  recordLine("Checklist version", d.checklistVersion);
  recordLine(
    "Export state",
    d.dirty
      ? "Includes local changes not uploaded to the saved scan"
      : "Uses the currently loaded scan data"
  );
  if (d.contact.name) recordLine("Contact name", d.contact.name);
  if (d.contact.email) recordLine("Contact email", d.contact.email);
  if (d.contact.phone) recordLine("Contact phone", d.contact.phone);
  layout.y += 6;
  layout.paragraph("Certificate", {
    size: 13,
    colour: C.ink,
    bold: true,
    gap: 7,
  });
  const cert = d.certificate;
  const certAssets = d.assets.filter(
    (a) => a.id === "certificate" || a.groupId === "certificate"
  );
  recordLine(
    "Evidence",
    certAssets.length
      ? certAssets.map((a) => a.ref).join(", ")
      : "No certificate file supplied"
  );
  recordLine(
    "Recorded choice",
    cert.status ? human(cert.status) : "Not recorded"
  );
  if (cert.rating)
    recordLine(
      "Text-screening result",
      cert.rating === "No"
        ? "No meaningful fire-rating text identified"
        : cert.rating
    );
  if (arr(cert.matches).length)
    recordLine("Matched text", cert.matches.join(", "));
  layout.paragraph(
    "A text match or an assumed rating is not independent verification of the door, certificate or installed assembly.",
    { size: 8.7, gap: 11 }
  );
  if (d.includeExtractedText && cert.text) {
    layout.paragraph("Extracted certificate text (unverified)", {
      size: 10,
      colour: C.ink,
      bold: true,
      gap: 6,
    });
    layout.paragraph(cert.text, { size: 8.8, gap: 14 });
  }
  layout.paragraph("Hinges", { size: 13, colour: C.ink, bold: true, gap: 7 });
  const positions = d.assets
    .filter((a) => a.id.startsWith("hinge_"))
    .map((a) => a.label);
  recordLine(
    "Photographs supplied",
    positions.length ? positions.join(", ") : "No hinge close-ups supplied"
  );
  if (d.hinges.reportedCount != null)
    recordLine(
      d.hinges.recordedAt
        ? "Reported number"
        : "Configured photo slots (not confirmed)",
      d.hinges.reportedCount
    );
  d.assets
    .filter((asset) => asset.groupId === "hinges")
    .forEach((asset) => {
      const screening = asset.textResult || d.hinges.textResults?.[asset.id];
      if (screening) {
        layout.paragraph(`${asset.ref} ${asset.label} - text screening`, {
          size: 9.8,
          colour: C.ink,
          bold: true,
          gap: 5,
        });
        recordLine(
          "Markings",
          arr(screening.matches).length
            ? screening.matches.join(", ")
            : "No recognised text matches"
        );
        layout.paragraph(
          "Text matches are not independent verification of hinge certification or suitability.",
          { size: 8.4, gap: 7 }
        );
      }
      if (d.includeExtractedText && asset.extractedText)
        layout.paragraph(
          `Extracted text (unverified): ${asset.extractedText}`,
          { size: 8.8, gap: 10 }
        );
    });
  layout.y += 7;
  layout.paragraph("Recorded answers and notes", {
    size: 13,
    colour: C.ink,
    bold: true,
    gap: 10,
  });
  if (!d.observations.length)
    layout.paragraph("No resident answers or notes were recorded.");
  d.observations.forEach((o) => {
    layout.ensure(52, null);
    const label =
      o.type === "checklist-answer"
        ? o.label
        : `${o.groupLabel || "Door"} - recorded note`;
    layout.paragraph(label, { size: 10, colour: C.ink, bold: true, gap: 4 });
    if (o.type === "checklist-answer")
      recordLine("Answer", o.answer || "Not recorded");
    if (o.text) layout.paragraph(o.text, { size: 9.4, colour: C.ink, gap: 5 });
    if (o.superseded)
      layout.paragraph(
        "Earlier or stale answer - not the current answer used by the checklist.",
        { size: 8.5, colour: C.review, gap: 5 }
      );
    if (o.recordedAt)
      layout.paragraph(`Recorded ${dateText(o.recordedAt, d.zone)}`, {
        size: 8,
        gap: 10,
      });
  });
  layout.y += 7;
  layout.ensure(125, null);
  layout.heading("One door. One review.", 18);
  layout.paragraph(d.reviewStatus, { size: 10, colour: C.ink, gap: 8 });
  if (d.receiptReference) recordLine("Receipt reference", d.receiptReference);
  if (d.companyContact) recordLine("Review contact", d.companyContact);
  layout.paragraph(
    "Return to the app to add missing evidence and submit the review request. A saved scan or downloaded PDF is not evidence that a professional review has been completed.",
    { size: 9.2, gap: 8 }
  );
  if (d.warnings.length) {
    layout.ensure(65, null);
    layout.heading("Export notes", 16);
    d.warnings.forEach((warning) =>
      layout.paragraph(warning, { size: 9, colour: C.review, gap: 7 })
    );
  }
}

export async function createDoorSnapshotPdf(snapshot, options = {}) {
  if (typeof document === "undefined" || typeof Blob === "undefined")
    throw new Error(
      "Generate the snapshot in a browser after the scan has loaded."
    );
  const { signal, onProgress = () => {} } = options;
  abortIfNeeded(signal);
  onProgress("Preparing the current scan...");
  const data = buildDoorSnapshotData(snapshot, options);
  const photos = data.assets.filter(
    (a) =>
      a.type === "photo" &&
      (data.includePhotoAppendix || ["front", "back"].includes(a.id))
  );
  for (let i = 0; i < photos.length; i++) {
    abortIfNeeded(signal);
    onProgress(`Preparing photo ${i + 1} of ${photos.length}...`);
    try {
      photos[i].raster = await rasterizePhoto(photos[i], {
        signal,
        maxEdge: options.maxImageEdge || 1600,
        timeout: options.photoTimeout || 18000,
      });
    } catch (error) {
      abortIfNeeded(signal);
      photos[i].loadError = clean(
        error.message || "This photo could not be embedded."
      );
      data.warnings.push(
        `${photos[i].ref} ${photos[i].label}: ${photos[i].loadError}`
      );
    }
    await nextFrame();
  }
  abortIfNeeded(signal);
  onProgress("Laying out the report...");
  const layout = new ReportLayout(data);
  drawCover(layout);
  await nextFrame();
  abortIfNeeded(signal);
  drawBreakdown(layout);
  await nextFrame();
  abortIfNeeded(signal);
  drawPhotoAppendix(layout);
  drawEvidenceRegister(layout);
  await nextFrame();
  abortIfNeeded(signal);
  drawRecords(layout);
  layout.footer();
  const blob = layout.pdf.toBlob(
    `${data.title} - ${data.doorName}`,
    data.company,
    data.generatedAt
  );
  if (blob.size > 50 * 1024 * 1024)
    throw new Error(
      "The PDF exceeds 50 MB. Export with a smaller maxImageEdge or without the photo appendix."
    );
  const safeName =
    (data.doorName || "door-scan")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 70) || "door-scan";
  const filename = `FireChecked-${safeName}-${data.generatedAt
    .toISOString()
    .slice(0, 10)}.pdf`;
  onProgress("PDF ready");
  return {
    blob,
    filename,
    pageCount: layout.pdf.pages.length,
    warnings: [...data.warnings],
    data,
    layoutAudit: layout.pdf.audit,
  };
}

export function useDoorSnapshotPdf({
  getSnapshot,
  resetKey = "",
  options = {},
}) {
  const latest = useRef({ getSnapshot, options });
  latest.current = { getSnapshot, options };
  const active = useRef(null),
    mounted = useRef(false),
    url = useRef("");
  const [state, setState] = useState({
    busy: false,
    message: "",
    error: "",
    warnings: [],
    downloadUrl: "",
    filename: "",
  });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.abort();
      if (url.current) URL.revokeObjectURL(url.current);
    };
  }, []);
  useEffect(() => {
    active.current?.abort();
    active.current = null;
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = "";
    setState({
      busy: false,
      message: "",
      error: "",
      warnings: [],
      downloadUrl: "",
      filename: "",
    });
  }, [resetKey]);
  const download = useCallback(async () => {
    if (active.current) return;
    const controller = new AbortController();
    active.current = controller;
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = "";
    setState({
      busy: true,
      message: "Preparing PDF...",
      error: "",
      warnings: [],
      downloadUrl: "",
      filename: "",
    });
    try {
      const snapshot = latest.current.getSnapshot();
      const result = await createDoorSnapshotPdf(snapshot, {
        ...latest.current.options,
        signal: controller.signal,
        onProgress: (message) => {
          if (mounted.current && active.current === controller)
            setState((s) => ({ ...s, message }));
        },
      });
      abortIfNeeded(controller.signal);
      if (!mounted.current || active.current !== controller) return;
      url.current = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url.current;
      anchor.download = result.filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setState({
        busy: false,
        message: `PDF ready - ${result.pageCount} pages.`,
        error: "",
        warnings: result.warnings,
        downloadUrl: url.current,
        filename: result.filename,
      });
    } catch (error) {
      if (mounted.current && active.current === controller)
        setState((s) => ({
          ...s,
          busy: false,
          message: "",
          error:
            error.name === "AbortError"
              ? ""
              : error.message ||
                "The PDF could not be created. Please try again.",
        }));
    } finally {
      if (active.current === controller) active.current = null;
    }
  }, []);
  return { ...state, download };
}
export function DoorScanPdfButton({ state, disabled = false }) {
  if (!state) return null;
  return (
    <div className="ds-pdf-export" aria-busy={state.busy}>
      <button
        type="button"
        className="ds-button ds-button-secondary"
        disabled={disabled || state.busy}
        onClick={state.download}
        aria-label="Download door snapshot PDF"
      >
        {state.busy ? "Creating PDF..." : "Download PDF"}
      </button>
      {state.message && (
        <p className="ds-muted" role="status" aria-live="polite">
          {state.message}
        </p>
      )}
      {state.error && (
        <p className="ds-inline-error" role="alert">
          {state.error}
        </p>
      )}
      {state.warnings.length > 0 && (
        <p className="ds-inline-error" role="status">
          {state.warnings.length} photo
          {state.warnings.length === 1 ? " was" : "s were"} unavailable in the
          export. The PDF includes the details; no missing image has been
          silently substituted.
        </p>
      )}
      {state.downloadUrl && (
        <a
          className="ds-text-button"
          href={state.downloadUrl}
          download={state.filename}
        >
          Download again
        </a>
      )}
    </div>
  );
}
export default DoorScanPdfButton;
